import { clearSessionCartRequest, placeOrderRequest } from '../cart/api';
import { fetchFoodCourtCartsRequest } from './api';

const initialState = {
  carts: [],
  status: 'idle', // 'idle' | 'loading' | 'success' | 'error'
  placeOrdersStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
  placedOrders: [],
  error: null,
};

export default {
  state: initialState,
  mutations: {
    setFoodCourtCarts(state, carts) {
      state.carts = carts;
    },
    setFoodCourtCartsStatus(state, status) {
      state.status = status;
    },
    setFoodCourtCartsError(state, error) {
      state.error = error;
    },
    clearFoodCourtCarts(state) {
      state.carts = [];
    },
    setPlaceOrdersStatus(state, status) {
      state.placeOrdersStatus = status;
    },
    setPlacedOrders(state, orders) {
      state.placedOrders = orders;
    },
  },
  actions: {
    async fetchFoodCourtCarts({ commit, getters }) {
      const foodCourtSession = getters.getFoodCourtSession;

      if (!foodCourtSession) {
        console.warn('No food court session available for fetching carts');
        return;
      }

      try {
        commit('setFoodCourtCartsStatus', 'loading');
        commit('setFoodCourtCartsError', null);

        const { data } = await fetchFoodCourtCartsRequest({ foodCourtSession });

        commit('setFoodCourtCarts', data);
        commit('setFoodCourtCartsStatus', 'success');
      } catch (error) {
        console.error('Error fetching food court carts:', error);
        commit(
          'setFoodCourtCartsError',
          error.message || 'Failed to fetch carts'
        );
        commit('setFoodCourtCartsStatus', 'error');
      }
    },
    async placeMultiMerchantOrders(
      { getters, commit, dispatch },
      { details } = {}
    ) {
      commit('setPlaceOrdersStatus', 'loading');

      const carts = getters.getFoodCourtCarts;
      const restaurantSessions = carts.map(cart => {
        return {
          session: cart.session,
          restaurantId: cart.restaurant_id,
        };
      });

      // Workaround for placing orders for multiple restaurants in a food court
      try {
        const orderPromises = restaurantSessions.map(
          ({ session, restaurantId }) =>
            placeOrderRequest({
              session,
              comment: '',
              tipAmount: 0,
              firebase_uid: getters.userId,
              details,
            }).catch(error => {
              error.restaurantId = restaurantId;
              throw error;
            })
        );

        const orders = await Promise.all(orderPromises);
        commit('setPlaceOrdersStatus', 'success');
        commit('setPlacedOrders', orders);
        console.log('All orders placed successfully:', JSON.stringify(orders));
        return orders;
      } catch (error) {
        console.error('Error placing multi-merchant orders:', error);
        const restaurant = getters.getFoodCourtRestaurantById(
          error.restaurantId
        );

        // Add restaurant name to error message if available
        if (restaurant) {
          error.restaurantName = restaurant.name;
        }

        dispatch('handlePlaceOrderError', error);
        throw error;
      } finally {
        commit('setPlaceOrdersStatus', 'idle');
        dispatch('fetchFoodCourtCarts');
      }
    },
    async clearFoodCourtCart({ dispatch, commit }, { sessions }) {
      if (!sessions || sessions.length === 0) return;

      const clearPromises = sessions.map(session =>
        clearSessionCartRequest({ session })
      );

      try {
        return await Promise.all(clearPromises);
      } catch (error) {
        console.error('Error clearing food court carts:', error);
        throw error;
      } finally {
        commit('clearFoodCourtCarts');
        commit('setFoodCourtCartsStatus', 'idle');
        commit('setFoodCourtCartsError', null);
        await dispatch('fetchFoodCourtCarts');
      }
    },
  },
  getters: {
    getFoodCourtCarts(state) {
      return state.carts;
    },
    getFoodCourtCartsStatus(state) {
      return state.status;
    },
    getFoodCourtCartsError(state) {
      return state.error;
    },
    getFoodCourtCartsLoading(state) {
      return state.status === 'loading';
    },
    getCartByRestaurantId: state => restaurantId => {
      return state.carts.find(cart => cart.restaurant_id === restaurantId);
    },
    getCartItemsByRestaurantId: state => restaurantId => {
      const restaurant = state.carts.find(
        cart => cart.restaurant_id === restaurantId
      );
      return restaurant ? restaurant.cart : [];
    },
    getCartItemById: state => cartItemId => {
      for (const restaurant of state.carts) {
        const item = restaurant.cart.find(
          item => item.cart_item_id === cartItemId
        );
        if (item) {
          return { ...item, restaurantId: restaurant.restaurant_id };
        }
      }
      return null;
    },
    getTotalCartItemsCount(state) {
      return state.carts.reduce((total, restaurant) => {
        return (
          total +
          restaurant.cart.reduce((restaurantTotal, item) => {
            return restaurantTotal + item.quantity;
          }, 0)
        );
      }, 0);
    },
    getSubtotalAmountInFoodCourtCart(state) {
      return state.carts.reduce((total, restaurant) => {
        return (
          total +
          restaurant.cart.reduce((restaurantTotal, item) => {
            let basePrice = item.price;

            if (item.loyalty_price !== null && item.loyalty_price !== false) {
              basePrice = item.loyalty_price;
            } else if (
              item.discounted_price !== null &&
              item.discounted_price !== false
            ) {
              basePrice = item.discounted_price;
            }

            let totalPrice = basePrice;
            if (item.options && item.options.length > 0) {
              item.options.forEach(modifier => {
                totalPrice += modifier.discounted_price || modifier.price || 0;
              });
            }

            return restaurantTotal + item.quantity * totalPrice;
          }, 0)
        );
      }, 0);
    },
    getCartItemsCountByRestaurant: state => restaurantId => {
      const restaurant = state.carts.find(
        cart => cart.restaurant_id === restaurantId
      );
      if (!restaurant) return 0;

      return restaurant.cart.reduce((total, item) => {
        return total + item.quantity;
      }, 0);
    },
    hasFoodCourtCartItems(state) {
      return state.carts.some(restaurant => restaurant.cart.length > 0);
    },
    hasCartItemsForRestaurant: state => restaurantId => {
      const restaurant = state.carts.find(
        cart => cart.restaurant_id === restaurantId
      );
      return restaurant ? restaurant.cart.length > 0 : false;
    },
    getRestaurantsWithCarts(state) {
      return state.carts.filter(restaurant => restaurant.cart.length > 0);
    },
    getPlaceOrdersInProgress(state) {
      return state.placeOrdersStatus === 'loading';
    },
    getPlacedOrders(state) {
      return state.placedOrders;
    },
    getPlaceOrdersIds(state) {
      return state.placedOrders.map(order => order.unique_order_number);
    },
  },
};
